import React, { ComponentProps, useMemo } from 'react';
import { OfferingCard } from '@ole-ui/ole-ui-components';

import { useTranslation } from 'react-i18next';
import { PremiumSessionAggregate } from 'types/premiumSession';
import usePremiumSessionAggregateInfo from 'hooks/usePremiumSessionAggregateInfo';
import { CourseLvcUserFacingStatus } from 'views/Catalog/useCourseLiveClassInfo';
import { OfferingVisibility } from 'types';
import { useOfferingCardContext } from '../OfferingCardContext';
import useBodyRowIcons from './hooks/useBodyRowIcons';
import useBodyRowCustom from './hooks/useBodyRowCustom';
import {
  BodyRowType,
  LiveVirtualClassInfo,
  ModalityExtension,
  SkillsAssessmentEntry,
} from '../utils/types';
import { getPremiumSessionTitle, getSkillsAssessmentRows } from './utils';

import '../styles.scss';
import offeringTypeRegistry from '../configs';
import { isCatalogEntry } from '../utils/typeGuards';

type BodyBuilderProps = {
  attendedSessionDate?: string;
  children?: React.ReactNode;
  premiumSessionAggregate?: PremiumSessionAggregate;
  schedulledSessionDate?: string;
  sessionModulesContent?: {
    [key: string]: number[];
  };
  liveVirtualClassInfo?: LiveVirtualClassInfo;
  customBadge?: string;
  availableLabel?: string;
} & ComponentProps<typeof OfferingCard.Body>;

const BodyBuilder = ({
  attendedSessionDate,
  children,
  premiumSessionAggregate,
  schedulledSessionDate,
  sessionModulesContent,
  liveVirtualClassInfo,
  title,
  customBadge,
  availableLabel,
  ...offeringCardBodyProps
}: BodyBuilderProps) => {
  const { i18n, t } = useTranslation();
  const {
    entry,
    hasEntry,
    hasProgress,
    learningPath,
    liveEventInfo,
    lvcStatus,
    offeringType,
    premiumSessionStatus,
    sessionCollateralData,
  } = useOfferingCardContext();

  const { hasSessionProgress, sessionsStatusList } =
    usePremiumSessionAggregateInfo({
      premiumSessionAggregate,
    });

  const catalogEntry = isCatalogEntry(entry) ? entry : undefined;

  const { buildIconRows } = useBodyRowIcons(
    catalogEntry,
    offeringType,
    hasProgress,
    learningPath,
  );

  const { buildCustomRows } = useBodyRowCustom({
    attendedSessionDate,
    entry: catalogEntry,
    premiumSessionStatus,
    schedulledSessionDate,
    sessionProgress: { hasSessionProgress, sessionsStatusList },
    learningPath,
    liveEventInfo,
    liveVirtualClassInfo,
    lvcStatus,
    offeringType,
  });

  const isSkillsAssessment =
    offeringType === ModalityExtension.SkillsAssessment;

  const translatedTitle = useMemo(() => {
    if (title) return title;

    if (isSkillsAssessment) {
      return (entry as SkillsAssessmentEntry)?.title;
    }

    if (
      offeringType === ModalityExtension.PremiumSession &&
      sessionModulesContent
    ) {
      const sessionTitle = getPremiumSessionTitle(sessionModulesContent, t);

      if (sessionTitle) return sessionTitle;
    }

    if (!hasEntry && sessionCollateralData?.offering_title) {
      return sessionCollateralData?.offering_title;
    }

    if (liveEventInfo) {
      return liveEventInfo.title;
    }

    return (
      catalogEntry?.translations?.find(
        (translation) => translation.language === i18n.language,
      )?.title || entry?.title
    );
  }, [
    entry?.title,
    catalogEntry?.translations,
    hasEntry,
    i18n.language,
    isSkillsAssessment,
    sessionCollateralData?.offering_title,
  ]);

  const customLabel = useMemo(() => {
    if (
      lvcStatus === CourseLvcUserFacingStatus.Available &&
      catalogEntry?.visibility === OfferingVisibility.Visible
    ) {
      return t('Dates available');
    }

    return '';
  }, [lvcStatus]);

  const bodyRows = useMemo(() => {
    if (isSkillsAssessment) {
      const saEntry = entry as SkillsAssessmentEntry;
      return getSkillsAssessmentRows(saEntry.timestamp, saEntry.totalScore, t);
    }

    const config = offeringTypeRegistry?.[offeringType];

    const bodyRowsLayout =
      config?.bodyRowsLayout({
        hasSessionProgress,
        learningPath,
        lvcStatus,
      }) ?? [];

    const content = bodyRowsLayout.reduce(
      (
        prevContent: ComponentProps<typeof OfferingCard.BodyRow>[],
        bodyRowType,
      ) => {
        if (bodyRowType === BodyRowType.Icons) {
          const iconRowsProps = buildIconRows();
          return [...prevContent, ...iconRowsProps];
        }

        if (bodyRowType === BodyRowType.Custom) {
          const customRowsProps = buildCustomRows();
          return [...prevContent, ...customRowsProps];
        }

        return prevContent;
      },
      [] as ComponentProps<typeof OfferingCard.BodyRow>[],
    );

    return content;
  }, [
    buildCustomRows,
    buildIconRows,
    entry,
    hasSessionProgress,
    isSkillsAssessment,
    learningPath,
    lvcStatus,
    offeringType,
    t,
  ]);

  const displayedSkuLabel = useMemo(() => {
    if (liveEventInfo) {
      return liveEventInfo.slug?.split('-')?.[0] || '';
    }

    return hasEntry
      ? catalogEntry?.code
      : premiumSessionAggregate?.content?.doc_id?.split('-')?.[0] || '';
  }, [liveEventInfo, hasEntry, entry, premiumSessionAggregate]);

  return (
    <OfferingCard.Body
      className="offering-card-builder__body"
      customLabel={customLabel}
      title={translatedTitle}
      skuLabel={displayedSkuLabel?.toUpperCase()}
      customBadge={customBadge}
      availableLabel={availableLabel}
      {...offeringCardBodyProps}
    >
      {bodyRows.map((rowProps) => (
        <OfferingCard.BodyRow {...rowProps} />
      ))}

      {children}
    </OfferingCard.Body>
  );
};

export default BodyBuilder;
